/**
 * Save format.
 *
 * Two decisions shape everything here.
 *
 * First, **only modified chunks are stored.** Terrain is deterministic from a
 * seed, so a saved game is the seed plus the differences a player has made.
 * A freshly generated world saves as a few hundred bytes, and the format keeps
 * working once the world streams infinitely and saving everything would be
 * impossible.
 *
 * Second, a save encodes to **one ArrayBuffer**, not to a JavaScript object.
 * That is what makes the storage backend interchangeable: the same bytes go
 * into IndexedDB today, and can be POSTed to a server, written to a file, or
 * handed to the player as a download without touching this file. See
 * platform/saveStore.ts for the interface that swap goes through.
 *
 * Pure data and byte-shuffling: no DOM, no engine, no storage.
 */

import { CHUNK_VOLUME } from "../world/chunk.js";
import type { ItemStack } from "../inventory/inventory.js";
import type { StoredContainer } from "../world/containers.js";
import type { StoredFurnace } from "../world/furnace.js";

/** One stack lying in the world. Velocity is not saved: it lands and rests. */
/**
 * A човен, as where it is and which way it is pointing.
 *
 * No id and no rider: ids are handed out afresh when a world is opened, and
 * nobody is standing in a boat in a file.
 */
export interface StoredBoat {
  x: number;
  y: number;
  z: number;
  yaw: number;
}

export interface DroppedStack {
  x: number;
  y: number;
  z: number;
  id: number;
  count: number;
  /**
   * How long this one is allowed to lie there.
   *
   * Written only when it is not the ordinary five minutes, which in practice
   * means a pile somebody died on. Absent in every save made before this
   * existed, and absent in most made since - so it needs no version bump: a
   * reader that does not find it uses the ordinary number, which is what
   * those saves meant.
   */
  keepFor?: number;
}
import type { MoveMode } from "../player/controller.js";
import type { Lang } from "../text/lang.js";

/**
 * Bumped whenever the layout below changes incompatibly.
 *
 * 2 added the crafting grid, 3 the spawn point, 4 what the chests hold, 5 what
 * is lying on the ground, 6 what the stoves are burning, 7 what is worn, 8
 * the човни floating about. Older
 * saves still load: an absent field means what that version had anyway - an
 * empty grid, a spawn chosen the way it always was, and no chests, which is
 * true of every world saved before chests existed. Old saves loading is the
 * whole reason this number exists.
 */
export const SAVE_FORMAT_VERSION = 10;

/**
 * Which terrain generator made this world.
 *
 * Separate from the format version and bumped for a different reason: the
 * format is about whether the bytes can be *read*, this is about whether they
 * still *mean* anything. A save stores the seed and the differences a player
 * made to what it grew; change how it grows and those differences are diffs
 * against terrain that no longer exists, so the old chunks come back as slabs
 * of somewhere else floating in the new world. That is exactly what happened
 * when the land became two islands, and it looked like a bug rather than like
 * a consequence.
 *
 * 1 is the two-island world, 2 added the orc lairs, 3 put clay lenses under
 * dry land, 4 put сосни on the high ground, 5 made the adventure four
 * thousand blocks across instead of one - which moves every column of it,
 * because where a column sits in the continent field depends on where the
 * middle of the map is. Anything saved before this existed reads as 0.
 *
 * **Bump this only when the change moves the ground.**
 *
 * That rule was learned the expensive way. Versions 2, 3 and 4 were all
 * bumped for changes that only *added* things - lairs, clay seams, pine trees
 * - and each one threw the player's world into the backup slot and started
 * them over with an empty bag. None of them needed to.
 *
 * The reason is in the format: a modified chunk is stored **whole**, not as a
 * difference against what generation would have made. So a save loaded by a
 * newer generator gets its buildings and its diggings back exactly, and only
 * the untouched land around them regrows. Adding a tree species to the high
 * ground is invisible to that; the worst it does is leave the new feature
 * missing inside chunks the player had already been standing in.
 *
 * What genuinely breaks is the surface *moving*: a хата built at y=70 over
 * land that is now sea, hanging in the air. That is what version 1 was for,
 * and it is the only shape of change that earns a bump.
 *
 * Six is the rivers, widened and deepened by half. Three of the thirteen
 * things changed with it - three times the iron, clay in the ground, and
 * земля along the water instead of only sand - and not one of those earns
 * anything: they change what a cell is *made of*, and a save carries its
 * own chunks whole. The river is different. A channel twenty-one blocks
 * across where it was fourteen lowers ground that used to be bank, and a
 * хата on that bank is a хата standing in a river.
 */
export const WORLDGEN_VERSION = 6;

/** "VYZH" - so a truncated or foreign file is rejected rather than misread. */
const MAGIC = 0x56595a48;

export interface SavedChunk {
  cx: number;
  cy: number;
  cz: number;
  blocks: Uint16Array;
}

export interface SaveGame {
  formatVersion: number;
  /** Epoch milliseconds, for showing "last played" in a slot list. */
  savedAt: number;
  /**
   * What the world has to be grown from again.
   *
   * `terrain` and `kremlin` were not here at first, and the world they
   * describe is not the world that comes back without them: a saved «Король
   * гори» reopened as two islands with a mountain on each, because
   * "islands" is what the generator does when nobody says otherwise. Both
   * are optional so that a save written before this loads at all - what
   * fills them in then is the map the slot belongs to, which is the same
   * answer by a longer road.
   */
  world: {
    seed: number; sizeXZ: number; sizeY: number;
    terrain?: "islands" | "hill" | "adventure" | "war" | "one";
    kremlin?: boolean;
  };
  /** Only chunks the player has changed. */
  chunks: SavedChunk[];
  player: {
    x: number; y: number; z: number;
    yaw: number; pitch: number;
    mode: MoveMode;
  };
  inventory: (ItemStack | null)[];
  /** Whatever was left sitting in the crafting grid. */
  craftGrid: (ItemStack | null)[];
  /**
   * What is being worn, in ARMOUR_SLOTS order.
   *
   * Its own field rather than four more inventory slots: armour is worn, not
   * carried, and a save written before it existed has to load as a bare body
   * rather than as four items shifted out of place.
   */
  armour: (ItemStack | null)[];
  /** What every chest in the world holds. Empty chests are not written. */
  containers: StoredContainer[];
  /**
   * Things lying on the ground.
   *
   * Saved, unlike the villagers and the arrows, because these are the
   * player's: a chestful tipped out and then saved over would otherwise be
   * gone, which is the exact loss pickups were added to prevent.
   */
  ground: DroppedStack[];
  /** Every stove with anything in it or anything alight. */
  furnaces: StoredFurnace[];
  /**
   * Човни left on the water.
   *
   * Saved for the same reason the ground is: a boat is the player's, made
   * out of their boards, and a world that forgets it every time it is closed
   * is a world that eats five planks a session. The livestock is not saved
   * and never will be - a herd is the country, and the country is the seed.
   */
  boats: StoredBoat[];
  /**
   * Пригоди already claimed, by id.
   *
   * Only the claimed ones. What is *finished but not yet claimed* is not
   * state at all - it is three стебла in the bag, and the bag is saved
   * already. Writing down progress as well would be writing the same fact
   * twice and giving the two of them a chance to disagree.
   */
  quests: string[];
  /**
   * Which лігва have had their Змій killed.
   *
   * "Після смерті не відроджується." A lair is arithmetic - forget the
   * country and walk back and the same hall is in the same mountain - so
   * "there is no dragon here any more" is the one fact about it that cannot
   * come out of the seed and has to be written down.
   *
   * Absent on every save made before dragons existed, which is exactly right
   * for them: no lair had been emptied.
   */
  slainLairs: string[];
  /** Which terrain generator grew this world. 0 for saves from before it existed. */
  worldgen: number;
  /** Time of day, 0..1. */
  time: number;
  skinId: string;
  /** Where the player wakes. Absent until a bed has been slept in. */
  spawn?: { x: number; y: number; z: number };
  /**
   * How many times the шопопало-блок has been hit open, on «Один блок».
   *
   * The drop table is walked in order, so this is the place in it. Without
   * it a world reopened tomorrow starts the table again and hands out the
   * same first six things - which on a map whose whole content is that table
   * is the difference between a game and a loop.
   *
   * Absent on every other map and on every save written before it existed,
   * and nought is the right answer for both.
   */
  oneBlockBreaks?: number;
}

/** Everything except the chunk voxels, which are appended as raw bytes. */
interface Header {
  savedAt: number;
  world: SaveGame["world"];
  chunkCoords: [number, number, number][];
  player: SaveGame["player"];
  inventory: (ItemStack | null)[];
  /** Absent in format 1. */
  craftGrid?: (ItemStack | null)[];
  /** Absent until format 3, and until a bed has been used. */
  spawn?: { x: number; y: number; z: number };
  /** Absent until format 4, and absent whenever every chest is empty. */
  containers?: StoredContainer[];
  /** Absent until format 5, and absent when the ground is clear. */
  ground?: DroppedStack[];
  /** Absent until format 6, and absent when every stove is cold and empty. */
  furnaces?: StoredFurnace[];
  /** Absent until format 8, and absent when nothing is afloat. */
  boats?: StoredBoat[];
  /** Absent until format 7, and absent on a player wearing nothing. */
  armour?: (ItemStack | null)[];
  /** Absent until format 9, and absent until a пригода has been claimed. */
  quests?: string[];
  /** Absent until a Змій has been killed, and in every save older than them. */
  slainLairs?: string[];
  /** Absent until format 10, and on every map but «Один блок». */
  oneBlockBreaks?: number;
  /** Absent in every save written before the terrain could change under one. */
  worldgen?: number;
  time: number;
  skinId: string;
}

/**
 * Encode a save to bytes.
 *
 * Layout: magic, format version, header length, JSON header (padded to an even
 * length so the voxel arrays stay 2-byte aligned), then each chunk's blocks
 * back to back in the order the header lists them.
 */
export function encodeSave(save: SaveGame): ArrayBuffer {
  const header: Header = {
    savedAt: save.savedAt,
    world: save.world,
    chunkCoords: save.chunks.map((c) => [c.cx, c.cy, c.cz]),
    player: save.player,
    inventory: save.inventory,
    craftGrid: save.craftGrid,
    ...(save.spawn !== undefined ? { spawn: save.spawn } : {}),
    // Left out entirely when there is nothing in any chest, so a world with a
    // hundred empty ones costs the save nothing.
    ...(save.containers.length > 0 ? { containers: save.containers } : {}),
    ...(save.ground.length > 0 ? { ground: save.ground } : {}),
    ...(save.boats.length > 0 ? { boats: save.boats } : {}),
    ...(save.furnaces.length > 0 ? { furnaces: save.furnaces } : {}),
    ...(save.armour.some((a) => a !== null) ? { armour: save.armour } : {}),
    ...(save.quests.length > 0 ? { quests: save.quests } : {}),
    ...(save.slainLairs.length > 0 ? { slainLairs: save.slainLairs } : {}),
    ...((save.oneBlockBreaks ?? 0) > 0
      ? { oneBlockBreaks: save.oneBlockBreaks } : {}),
    worldgen: save.worldgen,

    time: save.time,
    skinId: save.skinId,
  };

  const json = new TextEncoder().encode(JSON.stringify(header));
  // Uint16Array views need an even byte offset.
  const jsonPadded = json.length + (json.length % 2);

  const voxelBytes = save.chunks.length * CHUNK_VOLUME * 2;
  const buffer = new ArrayBuffer(12 + jsonPadded + voxelBytes);
  const view = new DataView(buffer);

  view.setUint32(0, MAGIC);
  view.setUint32(4, SAVE_FORMAT_VERSION);
  view.setUint32(8, json.length);
  new Uint8Array(buffer, 12, json.length).set(json);

  let offset = 12 + jsonPadded;
  for (const chunk of save.chunks) {
    new Uint16Array(buffer, offset, CHUNK_VOLUME).set(chunk.blocks);
    offset += CHUNK_VOLUME * 2;
  }
  return buffer;
}

export class SaveFormatError extends Error {}

/** Decode bytes produced by `encodeSave`. Throws if they are not a save. */
export function decodeSave(buffer: ArrayBuffer): SaveGame {
  if (buffer.byteLength < 12) throw new SaveFormatError("save is too short");

  const view = new DataView(buffer);
  if (view.getUint32(0) !== MAGIC) throw new SaveFormatError("not a Виживання save");

  const formatVersion = view.getUint32(4);
  if (formatVersion > SAVE_FORMAT_VERSION) {
    throw new SaveFormatError(
      `save is from a newer version of the game (format ${formatVersion})`,
    );
  }

  const jsonLength = view.getUint32(8);
  const jsonPadded = jsonLength + (jsonLength % 2);
  if (12 + jsonPadded > buffer.byteLength) {
    throw new SaveFormatError("save header is truncated");
  }

  const header = JSON.parse(
    new TextDecoder().decode(new Uint8Array(buffer, 12, jsonLength)),
  ) as Header;

  const chunks: SavedChunk[] = [];
  let offset = 12 + jsonPadded;
  for (const [cx, cy, cz] of header.chunkCoords) {
    if (offset + CHUNK_VOLUME * 2 > buffer.byteLength) {
      throw new SaveFormatError("save voxel data is truncated");
    }
    // Copy rather than view: the caller owns these arrays afterwards and the
    // backing buffer may be recycled by the storage layer.
    chunks.push({
      cx, cy, cz,
      blocks: new Uint16Array(buffer.slice(offset, offset + CHUNK_VOLUME * 2)),
    });
    offset += CHUNK_VOLUME * 2;
  }

  return {
    formatVersion,
    savedAt: header.savedAt,
    world: header.world,
    chunks,
    // A save written before chests existed has no chests, which is exactly
    // what an absent field should mean.
    containers: header.containers ?? [],
    ground: header.ground ?? [],
    boats: header.boats ?? [],
    furnaces: header.furnaces ?? [],
    // A save from before пригоди existed has claimed none, which is exactly
    // what an absent field should mean.
    quests: header.quests ?? [],
    // A save from before Змії existed has emptied no лігво, which is what an
    // absent field should mean here too.
    slainLairs: header.slainLairs ?? [],
    ...(header.oneBlockBreaks === undefined
      ? {} : { oneBlockBreaks: header.oneBlockBreaks }),
    worldgen: header.worldgen ?? 0,
    player: header.player,
    inventory: header.inventory,
    // Format 1 had no crafting grid; an empty one is what those worlds had.
    craftGrid: header.craftGrid ?? [],
    // Nobody wore anything before format 7, and nobody wearing nothing now
    // writes the field either - both mean the same bare body.
    armour: header.armour ?? [],
    // Absent means "never slept in a bed", which is what older worlds were.
    ...(header.spawn !== undefined ? { spawn: header.spawn } : {}),
    time: header.time,
    skinId: header.skinId,
  };
}

/**
 * Re-stamp a save as belonging to the terrain this build grows.
 *
 * This deliberately defeats the check above, and it is the only thing that
 * may. A save from an older generator is refused because loading one silently
 * puts a player in a world that is quietly wrong; a player who has been told
 * that and asks for it anyway is a different case, and without this the old
 * world in the backup slot is unreachable for ever.
 *
 * What actually comes back is worth being precise about: modified chunks are
 * stored whole, not as diffs, so everything built or dug returns exactly. It
 * is the untouched land around them that regrows to the new rules, and the
 * seam between the two is where a generator change shows.
 */
export function restampWorldgen(save: SaveGame): SaveGame {
  return { ...save, worldgen: WORLDGEN_VERSION };
}

/** Rough size of a save, for showing the player what it costs. */
export function saveSizeBytes(save: SaveGame): number {
  return encodeSave(save).byteLength;
}

/**
 * Ukrainian plural agreement: one form for 1, another for 2-4, a third for
 * 0 and 5+, with the teens taking the last form regardless of final digit.
 * Getting this wrong reads as broken text to a native speaker.
 */
export function pluralUk(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

/**
 * One line describing a save, for showing the player what they are about to
 * load. Pure string building, so the wording is testable.
 */
export function describeSave(save: SaveGame, lang: Lang = "uk"): string {
  const when = new Date(save.savedAt).toLocaleString();
  const n = save.chunks.length;
  // English pluralises by adding an s; Ukrainian needs three forms and a rule.
  const noun = lang === "en"
    ? (n === 1 ? "changed chunk" : "changed chunks")
    : pluralUk(n, "змінений чанк", "змінені чанки", "змінених чанків");
  return `${when} · ${n} ${noun}`;
}
